import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  TASK_CATS, catStyle, fmtDate, fmtDateShort, fmtTs, viewStatus, ST_STYLE, leftText, reminderDates, taskLink,
  hashTaskId, setTaskHash, loadMyTasks, loadTaskDetail, loadTms, loadSvUsers, createTasks, markOpened, reassign,
  markDone, confirmTask, returnTask, addComment, deleteTask, isActiveForMe, roleName, workdays,
} from '../lib/tasks';
import { mskToday, addDays } from '../lib/alerts';

const C = { border: '#E1E6F0', muted: '#55627A', blue: '#2E75B6', red: '#B3261E', green: '#1F7A44', ink: '#14213D' };
const card = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 };
const inp = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #D3DAE6', borderRadius: 10, fontSize: 14, fontFamily: 'inherit' };
const pill = (on) => ({ padding: '7px 13px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  border: on ? `2px solid ${C.blue}` : '1px solid #D3DAE6', background: on ? '#E8F1FA' : '#fff', color: on ? '#1A4F85' : C.ink, fontWeight: on ? 600 : 400 });
const btn = (bg = C.blue, fg = '#fff') => ({ padding: '10px 18px', background: bg, color: fg, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });
const Chip = ({ children, bg, fg }) => <span style={{ background: bg, color: fg, fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '3px 8px' }}>{children}</span>;
const Urgent = () => <span style={{ background: C.red, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', letterSpacing: .4 }}>СРОЧНО</span>;
const StatusPill = ({ s }) => { const [bg, fg] = ST_STYLE[s] || ['#eee', '#333']; return <span style={{ padding: '5px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' }}>{s}</span>; };

export default function TasksTab({ user, onChanged }) {
  const [data, setData] = useState({ tasks: [], subs: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [view, setView] = useState(() => hashTaskId() ? { name: 'detail', id: hashTaskId() } : { name: 'list' });
  const [tab, setTab] = useState('me');
  const [fCat, setFCat] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fUrgent, setFUrgent] = useState(false);
  const canCreate = user.role === 'rm' || user.role === 'tm';

  const reload = useCallback(async () => {
    try { setData(await loadMyTasks(user.id)); setErr(''); }
    catch (e) { setErr(e.message || 'Не удалось загрузить задачи'); }
    setLoading(false);
    onChanged && onChanged();
  }, [user.id, onChanged]);
  useEffect(() => { reload(); }, [reload]);

  const open = (id) => { setTaskHash(id); setView({ name: 'detail', id }); window.scrollTo({ top: 0 }); };
  const back = () => { setTaskHash(null); setView({ name: 'list' }); reload(); };

  if (view.name === 'detail') return <TaskDetail user={user} id={view.id} onBack={back} />;
  if (view.name === 'create') return <TaskCreate user={user} onCancel={() => setView({ name: 'list' })} onCreated={(t) => { reload(); setView({ name: 'created', tasks: t }); }} />;
  if (view.name === 'created') return <Created tasks={view.tasks} onOpen={open} onList={() => { setTab('by'); setView({ name: 'list' }); }} />;

  const { tasks, subs } = data;
  const today = mskToday();
  const isExec = (t) => t.assignee_id === user.id || subs.some(s => s.task_id === t.id && s.user_id === user.id);
  const meList = tasks.filter(t => isExec(t) && t.status !== 'done');
  const byList = tasks.filter(t => t.author_id === user.id && t.status !== 'done');
  const arch = tasks.filter(t => t.status === 'done');
  const base = tab === 'me' ? meList : tab === 'by' ? byList : arch;
  const list = base.filter(t => (fCat === 'all' || t.category === fCat)
    && (fStatus === 'all' || viewStatus(t, today) === fStatus) && (!fUrgent || t.urgent))
    .sort((a, b) => (b.urgent - a.urgent) || a.deadline.localeCompare(b.deadline));
  const meCount = tasks.filter(t => isActiveForMe(t, subs, user.id)).length;
  const cats = [...new Set(tasks.map(t => t.category))];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, flex: 1 }}>Мои задачи</h2>
        {canCreate && <button style={btn()} onClick={() => setView({ name: 'create' })}>+ {user.role === 'rm' ? 'Назначить' : 'Назначить команде'}</button>}
      </div>
      {err && <div style={{ ...card, color: C.red }}>{err}</div>}

      <div style={{ display: 'flex', background: '#EAEFF7', borderRadius: 10, padding: 3, overflowX: 'auto' }}>
        {[['me', `Назначенные мне · ${meList.length}`], ['by', `Назначил я · ${byList.length}`], ['arch', `Архив · ${arch.length}`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ flex: 1, whiteSpace: 'nowrap', padding: '8px 12px', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            background: tab === k ? '#fff' : 'transparent', fontWeight: tab === k ? 600 : 400, color: tab === k ? C.ink : C.muted, boxShadow: tab === k ? '0 1px 3px #14213D22' : 'none' }}>{l}</button>
        ))}
      </div>
      {meCount > 0 && tab !== 'me' && <div style={{ fontSize: 12, color: C.muted }}>Требуют действия: {meCount}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={fCat} onChange={e => setFCat(e.target.value)} style={{ ...inp, width: 'auto' }}>
          <option value="all">Категория: все</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ ...inp, width: 'auto' }}>
          <option value="all">Статус: все</option>
          {['Новая', 'В работе', 'Просрочена', 'Ждёт подтверждения', 'Подтверждена'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={fUrgent} onChange={e => setFUrgent(e.target.checked)} />Только срочные</label>
      </div>

      {tab === 'by' && byList.length > 0 && <Summary tasks={tasks.filter(t => t.author_id === user.id)} today={today} />}

      {loading ? <div style={card}>Загрузка…</div> : list.length === 0 ? (
        <div style={{ ...card, color: C.muted, textAlign: 'center' }}>{tab === 'me' ? 'Задач для вас нет' : tab === 'by' ? 'Вы пока ничего не назначали' : 'Архив пуст'}</div>
      ) : list.map(t => <TaskRow key={t.id} t={t} subs={subs} tab={tab} today={today} onOpen={() => open(t.id)} />)}
    </div>
  );
}

function TaskRow({ t, subs, tab, today, onOpen }) {
  const st = viewStatus(t, today);
  const [cb, cf] = catStyle(t.category);
  const mySubs = subs.filter(s => s.task_id === t.id);
  const late = st === 'Просрочена' || t.deadline === today;
  return (
    <div onClick={onOpen} style={{ ...card, cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', borderColor: t.urgent ? '#F2B8B5' : C.border }}>
      <div style={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {t.urgent && <Urgent />}<Chip bg={cb} fg={cf}>{t.category}</Chip>
          {t.repeat_weekly && <Chip bg="#EEF1F7" fg={C.muted}>Повтор: еженедельно</Chip>}
        </div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{t.title}</div>
        <div style={{ fontSize: 12, color: C.muted }}>
          {tab === 'by' ? `Кому: ${t.assignee_name}` : `От: ${roleName(t.author_role)} ${t.author_name}`}
          {mySubs.length > 0 && ` · переназначено: ${mySubs.map(s => s.user_name).join(', ')}`}
        </div>
      </div>
      <div style={{ minWidth: 100 }}>
        <div style={{ fontSize: 11, color: C.muted }}>Дедлайн</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: late ? C.red : C.ink }}>{fmtDate(t.deadline)}</div>
        <div style={{ fontSize: 11, color: late ? C.red : C.muted }}>{leftText(t, today)}</div>
      </div>
      <StatusPill s={st} />
    </div>
  );
}

function Summary({ tasks, today }) {
  const sts = tasks.map(t => viewStatus(t, today));
  const n = (s) => sts.filter(x => x === s).length;
  const monthAgo = addDays(today, -30);
  const doneM = tasks.filter(t => t.status === 'done' && (t.confirmed_at || '').slice(0, 10) >= monthAgo).length;
  const rows = {};
  tasks.forEach(t => {
    const r = rows[t.assignee_name] || (rows[t.assignee_name] = { w: 0, r: 0, l: 0, d: 0 });
    const s = viewStatus(t, today);
    if (s === 'Новая' || s === 'В работе') r.w++; else if (s === 'Просрочена') { r.w++; r.l++; } else if (s === 'Ждёт подтверждения') r.r++; else r.d++;
  });
  const Tile = ({ l, v, c }) => <div style={{ ...card, padding: '10px 14px', flex: '1 1 110px' }}><div style={{ fontSize: 11, color: C.muted }}>{l}</div><div style={{ fontSize: 24, fontWeight: 700, color: c }}>{v}</div></div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Tile l="В работе" v={n('Новая') + n('В работе') + n('Просрочена')} c="#C27A00" />
        <Tile l="Ждут подтверждения" v={n('Ждёт подтверждения')} c={C.blue} />
        <Tile l="Просрочено" v={n('Просрочена')} c={C.red} />
        <Tile l="Выполнено за 30 дн." v={doneM} c={C.green} />
      </div>
      <div style={{ ...card, overflowX: 'auto' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>По исполнителям</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 380 }}>
          <thead><tr style={{ textAlign: 'left', color: C.muted, fontSize: 11 }}><th style={{ padding: 6 }}>Исполнитель</th><th>В работе</th><th>На подтв.</th><th>Просрочено</th><th>Выполнено</th></tr></thead>
          <tbody>{Object.keys(rows).sort().map(k => { const r = rows[k]; return (
            <tr key={k} style={{ borderTop: '1px solid #EEF1F7' }}><td style={{ padding: 7, fontWeight: 600 }}>{k}</td><td>{r.w}</td><td>{r.r}</td>
              <td style={{ color: r.l ? C.red : C.muted, fontWeight: r.l ? 700 : 400 }}>{r.l}</td><td style={{ color: C.green }}>{r.d}</td></tr>); })}</tbody>
        </table>
      </div>
    </div>
  );
}

function Created({ tasks, onOpen, onList }) {
  const [copied, setCopied] = useState('');
  const copy = async (id) => { try { await navigator.clipboard.writeText(taskLink(id)); } catch (e) { window.prompt('Скопируйте ссылку', taskLink(id)); } setCopied(id); setTimeout(() => setCopied(''), 2000); };
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 18, color: C.green }}>Задача назначена ✓</h2>
      <div style={{ fontSize: 13, color: C.muted }}>Ссылку можно отправить в мессенджере или письме: после входа откроется именно эта задача.</div>
      {tasks.map(t => (
        <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid #EEF1F7' }}>
          <div style={{ flex: '1 1 160px', fontSize: 14 }}><b>{t.assignee_name}</b></div>
          <button style={btn('#EEF1F7', C.ink)} onClick={() => copy(t.id)}>{copied === t.id ? 'Скопировано ✓' : 'Скопировать ссылку'}</button>
          <button style={btn('#fff', C.blue)} onClick={() => onOpen(t.id)}>Открыть</button>
        </div>
      ))}
      <div><button style={btn()} onClick={onList}>К списку</button></div>
    </div>
  );
}

// ── форма ───────────────────────────────────────────────────────────────────
function MonthPicker({ value, onChange, min }) {
  const [m, setM] = useState(() => (value || min).slice(0, 7));
  const y = +m.slice(0, 4), mo = +m.slice(5, 7);
  const first = new Date(Date.UTC(y, mo - 1, 1)).getUTCDay();
  const lead = (first + 6) % 7;
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const shift = (d) => { const dt = new Date(Date.UTC(y, mo - 1 + d, 1)); setM(dt.toISOString().slice(0, 7)); };
  const names = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <button type="button" onClick={() => shift(-1)} style={{ ...btn('#EEF1F7', C.ink), padding: '4px 12px' }}>‹</button>
        <b style={{ fontSize: 14 }}>{names[mo - 1]} {y}</b>
        <button type="button" onClick={() => shift(1)} style={{ ...btn('#EEF1F7', C.ink), padding: '4px 12px' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, textAlign: 'center', fontSize: 11, color: '#7A869C' }}>
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => <span key={d}>{d}</span>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, textAlign: 'center', fontSize: 14, marginTop: 3 }}>
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const iso = `${m}-${String(d).padStart(2, '0')}`;
          const dis = iso < min, sel = iso === value;
          return <button key={i} type="button" disabled={dis} onClick={() => onChange(iso)} style={{ padding: '8px 0', border: 'none', borderRadius: 8, fontFamily: 'inherit', fontSize: 14,
            background: sel ? C.blue : iso === min ? '#EEF1F7' : 'transparent', color: sel ? '#fff' : dis ? '#C0C6D2' : C.ink, fontWeight: sel ? 700 : 400, cursor: dis ? 'default' : 'pointer' }}>{d}</button>;
        })}
      </div>
    </div>
  );
}

function TaskCreate({ user, onCancel, onCreated }) {
  const today = mskToday();
  const [title, setTitle] = useState(''); const [desc, setDesc] = useState('');
  const [cat, setCat] = useState('Актуализация инфы'); const [catOther, setCatOther] = useState('');
  const [link, setLink] = useState(''); const [urgent, setUrgent] = useState(false); const [repeat, setRepeat] = useState(false);
  const [deadline, setDeadline] = useState('');
  const [mode, setMode] = useState('all');   // all | pick | sv
  const [tms, setTms] = useState([]); const [svs, setSvs] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const isRm = user.role === 'rm';

  useEffect(() => {
    (async () => {
      try {
        if (isRm) { setTms((await loadTms()).map(x => ({ ...x, role: 'tm' }))); setSvs((await loadSvUsers(null)).map(x => ({ ...x, role: 'sv' }))); }
        else setSvs((await loadSvUsers(user.id)).map(x => ({ ...x, role: 'sv' })));
      } catch (e) { setErr(e.message); }
    })();
  }, [isRm, user.id]);

  const pool = isRm ? (mode === 'sv' ? svs : tms) : svs;
  const recipients = (isRm ? mode === 'all' : mode === 'all') ? pool : pool.filter(x => pick[x.id]);
  const rem = deadline ? reminderDates({ created_on: today, deadline }) : null;
  const tmName = useMemo(() => Object.fromEntries(tms.map(t => [t.id, t.full_name])), [tms]);
  const canSend = title.trim() && deadline && recipients.length && (cat !== 'Другое' || catOther.trim());

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const t = await createTasks({ user, recipients, title, description: desc, category: cat === 'Другое' ? `Другое: ${catOther.trim()}` : cat, link, urgent, repeat_weekly: repeat, deadline });
      onCreated(t);
    } catch (e) { setErr(e.message || 'Не удалось создать задачу'); setBusy(false); }
  };
  const L = ({ children }) => <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{children}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 20 }}>Новая задача</h2>
      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div><L>Название</L><input style={inp} value={title} onChange={e => setTitle(e.target.value)} maxLength={200} /></div>
        <div><L>Описание</L><textarea style={{ ...inp, minHeight: 80 }} value={desc} onChange={e => setDesc(e.target.value)} /></div>
        <div><L>Категория</L>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{TASK_CATS.map(c => <button key={c} type="button" style={pill(cat === c)} onClick={() => setCat(c)}>{c}{c === 'Другое' ? '…' : ''}</button>)}</div>
          {cat === 'Другое' && <input style={{ ...inp, marginTop: 8 }} placeholder="Впишите свою категорию" value={catOther} onChange={e => setCatOther(e.target.value)} maxLength={60} />}
        </div>
        <div><L>Ссылка для заполнения файлов (необязательно)</L><input style={inp} value={link} onChange={e => setLink(e.target.value)} placeholder="https://…" /></div>
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: urgent ? C.red : C.ink, fontWeight: urgent ? 700 : 400 }}><input type="checkbox" checked={urgent} onChange={e => setUrgent(e.target.checked)} />Срочно</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={repeat} onChange={e => setRepeat(e.target.checked)} />Повторять каждую неделю</label>
        </div>
        <div><L>Кому назначить</L>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={pill(mode === 'all')} onClick={() => setMode('all')}>{isRm ? `Все ТМ (${tms.length})` : `Вся команда (${svs.length})`}</button>
            <button type="button" style={pill(mode === 'pick')} onClick={() => setMode('pick')}>{isRm ? 'Выбрать ТМ…' : 'Выбрать СВ…'}</button>
            {isRm && <button type="button" style={pill(mode === 'sv')} onClick={() => setMode('sv')}>Выбрать СВ напрямую…</button>}
          </div>
          {(mode === 'pick' || mode === 'sv') && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
              {pool.map(x => <button key={x.id} type="button" style={pill(!!pick[x.id])} onClick={() => setPick(p => ({ ...p, [x.id]: !p[x.id] }))}>
                {x.full_name}{mode === 'sv' && x.tm_id && tmName[x.tm_id] ? ` · ${tmName[x.tm_id]}` : ''}</button>)}
            </div>)}
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Выбрано: {recipients.length}</div>
        </div>
        <div><L>Дедлайн</L><MonthPicker value={deadline} onChange={setDeadline} min={today} />
          {rem && <div style={{ background: '#F4F6FA', borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: 1.6, marginTop: 10 }}>
            {rem.mid ? <>Срок больше двух рабочих дней — будут напоминания:<br /><b>Промежуточное:</b> {fmtDate(rem.mid)}<br /></> : <>Срок короткий — только финальное напоминание.<br /></>}
            <b>Финальное:</b> {fmtDate(rem.fin)} (в день дедлайна)</div>}
        </div>
        {err && <div style={{ color: C.red, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ ...btn(), opacity: canSend && !busy ? 1 : .5 }} disabled={!canSend || busy} onClick={submit}>{busy ? 'Создаём…' : 'Назначить'}</button>
          <button style={btn('#fff', C.ink)} onClick={onCancel}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

// ── карточка ────────────────────────────────────────────────────────────────
function TaskDetail({ user, id, onBack }) {
  const [d, setD] = useState(null); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(''); const [cmt, setCmt] = useState(''); const [retNote, setRetNote] = useState('');
  const [team, setTeam] = useState([]); const [sel, setSel] = useState({}); const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try { setD(await loadTaskDetail(id, user.id)); } catch (e) { setErr(e.message); }
  }, [id, user.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.task && user.role === 'tm' && d.task.assignee_id === user.id) loadSvUsers(user.id).then(setTeam).catch(() => {});
    if (d?.task) markOpened(d.task, d.subs, user).then(ch => { if (ch) load(); }).catch(() => {});
    // eslint-disable-next-line
  }, [d?.task?.id]);

  if (err) return <div style={{ ...card, color: C.red }}>{err}<div><button style={btn('#fff', C.blue)} onClick={onBack}>‹ Назад</button></div></div>;
  if (!d) return <div style={card}>Загрузка…</div>;
  if (!d.task) return <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}><b>{d.denied ? 'Нет доступа к этой задаче' : 'Задача не найдена'}</b><div style={{ color: C.muted, fontSize: 13 }}>{d.denied ? 'Она назначена другим участникам.' : 'Возможно, её удалили.'}</div><div><button style={btn()} onClick={onBack}>К моим задачам</button></div></div>;

  const { task: t, subs, comments, history } = d;
  const today = mskToday();
  const st = viewStatus(t, today);
  const [cb, cf] = catStyle(t.category);
  const isAuthor = t.author_id === user.id;
  const isAssignee = t.assignee_id === user.id;
  const mySub = subs.find(s => s.user_id === user.id);
  const canDone = t.status === 'open' && ((isAssignee) || (mySub && mySub.status !== 'done'));
  const canReassign = isAssignee && user.role === 'tm' && t.status === 'open';
  const rem = reminderDates(t);
  const run = async (fn) => { setBusy(true); setErr(''); try { await fn(); await load(); } catch (e) { setErr(e.message || 'Ошибка'); } setBusy(false); };
  const copy = async () => { try { await navigator.clipboard.writeText(taskLink(t.id)); } catch (e) { window.prompt('Скопируйте ссылку', taskLink(t.id)); } setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const subState = { new: ['Новая', ST_STYLE['Новая']], in_work: ['В работе', ST_STYLE['В работе']], done: ['Выполнено', ST_STYLE['Подтверждена']] };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.blue, fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}>‹ Мои задачи</button>
        <div style={{ flex: 1 }} />
        <button style={{ ...btn('#EEF1F7', C.ink), padding: '7px 14px', fontSize: 13 }} onClick={copy}>{copied ? 'Скопировано ✓' : '🔗 Скопировать ссылку'}</button>
      </div>

      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {t.urgent && <Urgent />}<Chip bg={cb} fg={cf}>{t.category}</Chip><StatusPill s={st} />
          {t.repeat_weekly && <Chip bg="#EEF1F7" fg={C.muted}>Повтор: еженедельно</Chip>}
        </div>
        <h2 style={{ margin: 0, fontSize: 20 }}>{t.title}</h2>
        {t.description && <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{t.description}</div>}
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', fontSize: 13 }}>
          <div><div style={{ color: C.muted }}>Поставил</div><b>{roleName(t.author_role)} {t.author_name}</b></div>
          <div><div style={{ color: C.muted }}>Исполнитель</div><b>{roleName(t.assignee_role)} {t.assignee_name}</b></div>
          <div><div style={{ color: C.muted }}>Дедлайн</div><b style={{ color: st === 'Просрочена' || t.deadline === today ? C.red : C.ink }}>{fmtDate(t.deadline)}</b></div>
          <div><div style={{ color: C.muted }}>Напоминания</div><b>{rem.mid ? `промежуточное ${fmtDateShort(rem.mid)} · ` : ''}финальное {fmtDateShort(rem.fin)}</b></div>
        </div>
        {t.link && <div style={{ background: '#F4F6FA', borderRadius: 10, padding: '10px 12px', fontSize: 13, wordBreak: 'break-all' }}>Ссылка для заполнения: <a href={/^https?:\/\//i.test(t.link) ? t.link : `https://${t.link}`} target="_blank" rel="noreferrer" style={{ color: C.blue, fontWeight: 600 }}>{t.link}</a></div>}
        {t.result && <div style={{ background: '#EAF6EF', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}><b>Результат:</b> {t.result}</div>}
      </div>

      {isAuthor && t.status === 'review' && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10, borderColor: C.blue }}>
          <b>Исполнитель отметил «Выполнено»</b>
          <input style={inp} placeholder="Комментарий при возврате (необязательно)" value={retNote} onChange={e => setRetNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button disabled={busy} style={btn(C.green)} onClick={() => run(() => confirmTask(t, user))}>Подтвердить</button>
            <button disabled={busy} style={btn('#fff', C.red)} onClick={() => run(() => returnTask(t, user, retNote))}>Вернуть на доработку</button>
          </div>
        </div>
      )}

      {canReassign && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}><b style={{ flex: 1 }}>Переназначить на мою команду</b><span style={{ fontSize: 12, color: C.muted }}>Задача у меня остаётся, пока я не отмечу «Выполнено»</span></div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {team.filter(u => !subs.some(s => s.user_id === u.id)).map(u => <button key={u.id} style={pill(!!sel[u.id])} onClick={() => setSel(s => ({ ...s, [u.id]: !s[u.id] }))}>{u.full_name}</button>)}
            {team.length > 0 && <button style={pill(false)} onClick={() => { const all = {}; team.forEach(u => { if (!subs.some(s => s.user_id === u.id)) all[u.id] = true; }); setSel(all); }}>Все СВ</button>}
          </div>
          <div><button disabled={busy || !Object.values(sel).some(Boolean)} style={{ ...btn(), opacity: Object.values(sel).some(Boolean) ? 1 : .5 }}
            onClick={() => run(async () => { await reassign(t, team.filter(u => sel[u.id]), user); setSel({}); })}>Переназначить</button></div>
        </div>
      )}
      {subs.length > 0 && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <b>Переназначено на СВ</b>
          {subs.map(s => { const [l, [bg, fg]] = subState[s.status] || subState.new; return (
            <div key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', border: '1px solid #EEF1F7', borderRadius: 10 }}>
              <span style={{ flex: '1 1 140px', fontWeight: 600, fontSize: 14 }}>{s.user_name}</span>
              <span style={{ fontSize: 12, color: C.muted }}>{s.result || (s.opened_at ? `открыл(а) ${fmtTs(s.opened_at)}` : 'ещё не открывал(а)')}</span>
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: bg, color: fg }}>{l}</span></div>); })}
        </div>
      )}

      {canDone && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <b>Результат</b>
          <input style={inp} placeholder="Ссылка на результат или комментарий (необязательно)" value={result} onChange={e => setResult(e.target.value)} />
          <div><button disabled={busy} style={btn(C.green)} onClick={() => run(() => markDone(t, user, result))}>Выполнено</button></div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            {isAssignee ? 'После этого задача уйдёт постановщику на подтверждение. Он может подтвердить её или вернуть на доработку.' : 'Ваш ТМ увидит, что вы выполнили. Подтверждает постановщик.'}</div>
        </div>
      )}

      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <b>Комментарии</b>
        {comments.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>Пока нет комментариев</div>}
        {comments.map(m => <div key={m.id} style={{ background: '#F8FAFD', borderRadius: 10, padding: '8px 12px' }}>
          <div style={{ fontSize: 12, color: C.muted }}><b style={{ color: C.ink }}>{m.user_name} ({roleName(m.user_role)})</b> · {fmtTs(m.created_at)}</div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{m.body}</div></div>)}
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={inp} placeholder="Написать комментарий…" value={cmt} onChange={e => setCmt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && cmt.trim()) run(async () => { await addComment(t.id, user, cmt); setCmt(''); }); }} />
          <button disabled={busy || !cmt.trim()} style={{ ...btn(), opacity: cmt.trim() ? 1 : .5 }} onClick={() => run(async () => { await addComment(t.id, user, cmt); setCmt(''); })}>Отправить</button>
        </div>
      </div>

      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <b>История</b>
        {history.map(h => <div key={h.id} style={{ display: 'flex', gap: 10, fontSize: 13 }}><span style={{ color: '#7A869C', minWidth: 96 }}>{fmtTs(h.created_at)}</span><span>{h.text}</span></div>)}
      </div>

      {isAuthor && <div><button style={{ ...btn('#fff', C.red), border: '1px solid #F2B8B5' }} disabled={busy}
        onClick={() => { if (window.confirm('Удалить задачу целиком?')) run(async () => { await deleteTask(t); onBack(); }); }}>Удалить задачу</button></div>}
      {err && <div style={{ color: C.red, fontSize: 13 }}>{err}</div>}
    </div>
  );
}
